/**
 * Employment Change Log report (ADR-145).
 *
 * Built on the reports family's furniture on purpose — header stats, bare
 * FiltersBar, ResultsCount + SortButton, EmployeeRow-shaped rows with an
 * avatar and a name link, the shared LoadMore and pastel chips. A report that
 * looks unlike its neighbours reads as a different product.
 *
 * Four things this component is responsible for getting right, all of them
 * decisions rather than styling:
 *
 *  §2  the row is a change record, so the header reads changes AND people —
 *      one number cannot tell a mass entity migration apart from one confused
 *      operator.
 *  §6  machine rows are MUTED, not hidden. They outnumber human rows 5-10x
 *      every active month; hiding them lies about the system, showing them
 *      undifferentiated buries the handful that matter.
 *  §8  the summary's aggregation boundary is PRINTED, not implied.
 *  §5.4 while the effective-date filter is on, the view SAYS it is excluding
 *      undated rows — which is most of the table.
 *
 * The field taxonomy comes from the server's field-catalog: this component
 * never restates which fields are contract terms.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaArrowLeft, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import {
  FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText,
  FormControlLabel, Switch, Chip, Tooltip, Alert, OutlinedInput, CircularProgress, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { Container, Header, HeaderLeft, Title, Content, MainSection, BackButton } from '../styles';
import {
  FiltersBar, HeaderStats, HeaderStat, IntegrityPanel, AmendmentsLanePanel,
  ChangeRow, ChangeRowMain, ChangeInfo, ChangeMetaCell, CorrelationGroup, CorrelationNote,
  DetailPanel, LoadMoreButton, SortButton, FootNote
} from './styles';
import { EmployeeAvatar } from '../../common/EmployeeAvatar';
import ActorChip from './ActorChip';
import { actorLabel, actorDetail } from './actorDisplay';
import {
  contractLabel, ownershipLabel, showPrimaryChip, lacksNumber, amendmentHeading
} from './contractDisplay';
import { contractIdentityLabel } from '../../../constants/contractActions';
import { contractNumberGate } from '../../../utils/contractNumber';
import { useAbility } from '../../../contexts/AbilityContext';
import { employmentApi } from '../../../services/api';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import ResultsCount from '../../common/ResultsCount';
import EmployeeSearch from '../../common/EmployeeSearch';
import { EmployeeSearchProvider } from '../../common/EmployeeSearch/contexts/EmployeeSearchProvider';
import ClusterSelector from '../../common/ClusterSelector';
import BusinessUnitSelector from '../../common/BusinessUnitSelector';
import EmployeeReportingScope from '../../common/filters/EmployeeReportingScope';
import { EMPLOYEE_REPORTING_SCOPE } from '../../../constants/employeeReportingScope';
import { getChipSx } from '../../../constants/chipPalette';
import { useAlert } from '../../../contexts/AlertContext';
import { employmentChangeLogApi } from '../../../services/api/employmentChangeLogApi';
import {
  FIELD_GROUP_LABELS, FIELD_GROUP_HINTS, DETECTOR_OPTIONS, DETECTOR_LABELS,
  ACTOR_TYPE_OPTIONS, OWNERSHIP_OPTIONS, EXPLANATION_OPTIONS, WINDOW_OPTIONS,
  STATUS_OPTIONS, DEFAULT_FILTERS,
  AMENDMENTS_LANE_OPTIONS, AMENDMENT_CANCEL_CAUSE_LABELS
} from '../../../constants/employmentChangeLog';
import { formatDateOnly } from '../../../utils/dateOnly';

const PAGE_SIZE = 50;

const formatDateTime = (value) => new Date(value).toLocaleString('en-GB', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

const formatDate = (value) => (value
  ? new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
  : null);
// ADR-197: effectiveOn and contract dates are date-only — the stored calendar
// day. formatDate above stays for instants (raisedAt, cancelled.at).
const formatDay = (value) => formatDateOnly(value, 'DD/MM/YYYY', '') || null;

/**
 * §9.3 — a deleted referent is FLAGGED, never blank; an absent value says why
 * it is absent through the detector rather than through an empty cell.
 */
const ValueText = ({ value, detector, side }) => {
  if (!value) {
    // ADR-152 §5.2 — an absent PREVIOUS has three meanings and the row cannot
    // tell them apart on its own, so the detector says which. A hire-machinery
    // row is an ESTABLISHMENT: the contract is new, so there is no earlier value
    // ON THIS CONTRACT — the person may well have had one on the contract this
    // one replaced. Saying "the field held no value" there would be false.
    const why = side === 'previous'
      ? (detector === 'nightly-sweep'
        ? 'No baseline value — the sweep had nothing recorded before this.'
        : detector === 'hire-machinery'
          ? 'A new contract — there is no earlier value on this contract.'
          : 'The field held no value before this write.')
      : 'The value was cleared.';
    return <Tooltip title={why}><span className="absent">—</span></Tooltip>;
  }
  if (value.deleted) {
    return (
      <Tooltip title={`The referenced record no longer exists. Id ${value.id}`}>
        <span className="gone">«deleted»</span>
      </Tooltip>
    );
  }
  return <span className={side === 'previous' ? 'from' : 'to'}>{value.label}</span>;
};

const EXPLAINING_EVENT_LABELS = {
  'employment.contract.amended': 'Amendment',
  'employment.resign.submitted': 'Resignation scheduled/moved',
  'employment.resign.finalized': 'Scheduled end recorded',
  'employment.resign.withdrawn': 'Scheduled end withdrawn',
  // ADR-152 — the hire machinery's three explaining events. Without these the
  // row falls back to the raw code, which is the one place a reader meets it.
  'employment.hire.rehire': 'Contract replaced',
  'employment.hire.finalized': 'Contract started',
  'employment.hire.start_delayed': 'Start delayed',
};

const explainingEventLabel = (explainedBy) => {
  if (!explainedBy) { return null; }
  return EXPLAINING_EVENT_LABELS[explainedBy.eventTypeCode] || explainedBy.eventTypeCode || 'Employment event';
};

const detailNote = (row) => {
  if (row.explained && row.detector === 'resign-machinery') {
    return 'The contract\u2019s scheduled end moved through the resignation machinery — explained by the resign event that carried it.';
  }
  // ADR-152 — the hire machinery. Two different facts share the detector, and
  // the presence of a previous value is what tells them apart: an establishment
  // has none (a new contract), a delay has one (the same contract moved).
  if (row.explained && row.detector === 'hire-machinery') {
    return row.previous
      ? 'This contract\u2019s start moved — explained by the start-delay event that carried it.'
      : 'A new contract began on this date. There is no earlier value because this is the contract\u2019s first — the predecessor, if any, is a separate contract with its own history.';
  }
  // ADR-164 D2 — say what a correction is, before any other reading of it.
  if (row.isCorrection) {
    return 'A correction, not a change. The position history already said this from the effective date; the stored value had fallen behind it and has now caught up. Not counted as a change.';
  }
  if (row.detector === 'position-schedule') {
    return 'A position or grade change scheduled in advance took effect on its day. Its effective date is the day the history says it starts.';
  }
  if (row.explained) {
    return 'Written by the amendment applier as it applied the amendment, and attributed to the operator who raised it — not to whoever ran the sweep.';
  }
  if (row.unexplainedTerm && row.actor?.type === 'User') {
    return 'A contract term moved with no employment event behind it at all. On a radar-owned contract that is a finding: the term changed without the instrument that should record it.';
  }
  if (row.actor?.type === 'System' && row.contract?.ownership === 'sync') {
    return 'Expected drift. The contract is stamped to an ingestion source, which stays authoritative for its own records and reconciles on its next run.';
  }
  if (row.detector === 'nightly-sweep') {
    return 'Observed by the weekday sweep, which records when it noticed. No effective date is invented for derived movement.';
  }
  return 'Observed by a write hook against the prior value, in the same transaction as the write it observes.';
};

// ADR-112 — an amendment row renders payload-locally: old -> new labels come
// from the `display` map frozen at signing time, with the raw payload values
// as fallback (dates, or ids for pre-display-era rows). The field label carries
// the same emphasis as the record cards' detail line — it is what the intent is
// ABOUT — so the phrase is a node list, not a joined string.
const amendmentPhrases = (row, fieldLabel) =>
  Object.keys(row.changes || {}).map((field, indexInPhrase) => {
    const d = row.display?.[field] || {};
    const prev = d.previous ?? row.previous?.[field] ?? '—';
    const next = d.next ?? row.changes?.[field] ?? '—';
    return (
      <React.Fragment key={field}>
        {indexInPhrase > 0 && ' · '}
        <span className="field">{fieldLabel(field)}</span>
        {`: ${prev} → ${next}`}
      </React.Fragment>
    );
  });

const EmploymentChangeLogReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();

  // §6 — all filters are restored on return, not just the origin, following
  // the ftcState pattern (ADR-090). Deliberately NOT location.state.returnTo:
  // on this page that key means "where the EMPLOYEE profile returns to".
  const restored = location.state?.changeLogState;
  const backTo = restored?.backTo || '/reports';
  const backLabel = restored?.backLabel || 'Reports';

  const [filters, setFilters] = useState(restored?.filters || DEFAULT_FILTERS);
  // Cards vs table: the card list carries the report's reading devices (the
  // correlation band, the severity stripe, row expansion); the table is the
  // compact scan view the retired admin page used to provide. Same rows,
  // same filters — presentation only.
  const [viewMode, setViewMode] = useState(restored?.viewMode === 'table' ? 'table' : 'cards');
  const [selectedEmployee, setSelectedEmployee] = useState(restored?.selectedEmployee || null);
  const [representative, setRepresentative] = useState(restored?.representative || null);
  const [representativeScope, setRepresentativeScope] = useState(
    restored?.representativeScope || EMPLOYEE_REPORTING_SCOPE.DIRECT
  );

  const [catalog, setCatalog] = useState(null);
  const [report, setReport] = useState(null);
  const [rows, setRows] = useState([]);
  const [pageInfo, setPageInfo] = useState({ hasNextPage: false, endCursor: null });
  const [isLoading, setIsLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  // ADR-112 — the amendments lane. EVENT-sourced, fetched separately from
  // the record list (no cursor: Pending amendments are a small set by
  // nature, and the server caps the lane).
  const [amendments, setAmendments] = useState(null);
  const ability = useAbility();
  // ADR-174 §7 — the contract-number box commits on Enter/blur, not per keystroke
  const [contractNumberInput, setContractNumberInput] = useState(restored?.filters?.contractNumber || '');
  // ADR-174 §7 — the picked individual's contracts, for the Contract picker
  const [personContracts, setPersonContracts] = useState([]);

  useEffect(() => {
    employmentChangeLogApi.getFieldCatalog()
      .then(setCatalog)
      .catch(() => setCatalog(null));
  }, []);

  const load = useCallback(async ({ cursor } = {}) => {
    try {
      setIsLoading(true);
      const result = await employmentChangeLogApi.getEmploymentChangeLogReport({
        ...filters,
        employeeId: selectedEmployee?.id,
        representativeId: representative?.id,
        reportingScope: representative ? representativeScope : undefined,
        limit: PAGE_SIZE,
        cursor,
      });
      setReport(result);
      setRows((prev) => (cursor ? [...prev, ...result.items] : result.items));
      setPageInfo(result.pageInfo);
    } catch (error) {
      showAlert('Failed to load the employment change log', 'error', error);
    } finally {
      setIsLoading(false);
    }
  }, [filters, selectedEmployee, representative, representativeScope, showAlert]);

  useEffect(() => { load(); }, [load]);

  const amendmentsLane = filters.amendmentsLane || 'hidden';

  const loadAmendments = useCallback(async () => {
    if (amendmentsLane === 'hidden') {
      setAmendments(null);
      return;
    }
    try {
      const lane = await employmentChangeLogApi.getEmploymentChangeAmendments({
        clusterId: filters.clusterId || undefined,
        includeSubUnits: filters.includeSubUnits,
        businessUnitId: filters.businessUnitId || undefined,
        buScope: filters.buScope,
        employeeId: selectedEmployee?.id,
        representativeId: representative?.id,
        reportingScope: representative ? representativeScope : undefined,
        organizationalStatus: filters.organizationalStatus || undefined,
        fields: filters.fields,
        fieldGroups: filters.fieldGroups,
        contractNumber: filters.contractNumber,
        employmentEpisodeIds: filters.employmentEpisodeIds,
        concurrentOnly: filters.concurrentOnly,
        windowDays: filters.windowDays,
        includeCancelled: amendmentsLane === 'all',
      });
      setAmendments(lane);
    } catch (error) {
      showAlert('Failed to load the amendments lane', 'error', error);
    }
  }, [amendmentsLane, filters, selectedEmployee, representative, representativeScope, showAlert]);

  useEffect(() => { loadAmendments(); }, [loadAmendments]);

  // ADR-174 §7 — the Contract picker lists the selected individual's
  // contracts; picking another individual drops a stale contract choice.
  const selectedEmployeeId = selectedEmployee?.id || null;
  useEffect(() => {
    let cancelled = false;
    if (!selectedEmployeeId) {
      setPersonContracts([]);
      setFilters((prev) => (prev.employmentEpisodeIds?.length ? { ...prev, employmentEpisodeIds: [] } : prev));
      return undefined;
    }
    employmentApi.getEmploymentEpisodes(selectedEmployeeId)
      .then((episodes) => {
        if (cancelled) return;
        const list = Array.isArray(episodes) ? episodes : [];
        setPersonContracts(list);
        setFilters((prev) => {
          const kept = (prev.employmentEpisodeIds || []).filter((id) => list.some((e) => e.id === id));
          return kept.length === (prev.employmentEpisodeIds || []).length ? prev : { ...prev, employmentEpisodeIds: kept };
        });
      })
      .catch(() => { if (!cancelled) setPersonContracts([]); });
    return () => { cancelled = true; };
  }, [selectedEmployeeId]);

  const commitContractNumber = () => {
    const next = contractNumberInput.trim();
    if (next !== (filters.contractNumber || '')) {
      setFilters((prev) => ({ ...prev, contractNumber: next }));
    }
  };

  // ADR-174 §3.3 — the "add number" hint, for a viewer who may set it.
  const canAddNumber = (contract) => lacksNumber(contract) && contractNumberGate(ability, {
    endDate: contract.endDate,
    payload: contract.source ? { sourceReference: { source: contract.source } } : null,
  }).allowed;

  const openContractsTab = (row) => {
    if (!row.employee?.id) return;
    navigate(`/employees/${row.employee.id}/view?tab=CONTRACTS`, {
      state: {
        returnTo: '/reports/employment-changes',
        changeLogState: {
          filters, selectedEmployee, representative, representativeScope,
          viewMode, backTo, backLabel,
        },
      },
    });
  };

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // ADR-172 D5 — the people who changed something in this population/period.
  // A picked person stays selectable even if a later window no longer has them.
  const actorOptions = useMemo(() => {
    const options = report?.summary?.actors || [];
    if (filters.actorId && !options.some((o) => o.value === filters.actorId)) {
      return [...options, { value: filters.actorId, displayName: null, email: filters.actorId, records: 0 }];
    }
    return options;
  }, [report, filters.actorId]);

  const fieldLabel = useCallback(
    (field) => catalog?.fields.find((f) => f.field === field)?.label || field,
    [catalog]
  );

  const fieldCountByGroup = useMemo(() => {
    if (!catalog) return {};
    return catalog.fields.reduce((acc, f) => ({ ...acc, [f.group]: (acc[f.group] || 0) + 1 }), {});
  }, [catalog]);

  // ADR-145 §2/§6 — the correlation grouping behind the ledger-margin band:
  // consecutive rows sharing a correlationId are one cause, rendered as one
  // banded group with the cause stated once between the rows.
  const rowGroups = useMemo(() => {
    const groups = [];
    rows.forEach((row) => {
      const last = groups[groups.length - 1];
      if (last && row.correlationId && last.correlationId === row.correlationId) {
        last.rows.push(row);
      } else {
        groups.push({ key: row.id, correlationId: row.correlationId, rows: [row] });
      }
    });
    return groups;
  }, [rows]);

  const summary = report?.summary;
  const meta = report?.meta;

  const handleNavigateToEmployee = (row) => {
    if (!row.employee?.id) return;
    navigate(`/employees/${row.employee.id}/view`, {
      state: {
        returnTo: '/reports/employment-changes',
        changeLogState: {
          filters, selectedEmployee, representative, representativeScope,
          viewMode, backTo, backLabel,
        },
      },
    });
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate(backTo)}>
            <FaArrowLeft /> Back to {backLabel}
          </BackButton>
          <Title>Employment Change Log</Title>
        </HeaderLeft>
        <HeaderStats>
          {isLoading && !summary ? (
            <CircularProgress size={20} />
          ) : summary && (
            <>
              {/* §2 — changes AND people, always. One of them alone cannot tell
                  a mass entity migration apart from one confused operator. */}
              <HeaderStat>
                <div className="top">
                  <span className="label">Changes</span>
                  <span className="value">{summary.changes}</span>
                </div>
              </HeaderStat>
              <HeaderStat>
                <div className="top">
                  <span className="label">People</span>
                  <span className="value">{summary.employeesAffected}</span>
                </div>
              </HeaderStat>
              <HeaderStat $alarm $clear={summary.unexplainedTermsByHuman === 0}>
                <div className="top">
                  <span className="label">Unexplained</span>
                  <span className="value">{summary.unexplainedTerms}</span>
                </div>
                <span className="sub">{summary.unexplainedTermsByHuman} by a human</span>
              </HeaderStat>
              <HeaderStat>
                <div className="top">
                  <span className="label">Machine</span>
                  <span className="value">{summary.machineShare}%</span>
                </div>
                <span className="sub">Expected sync drift</span>
              </HeaderStat>
              {/* ADR-164 D2 — corrections are shown, never counted as changes. */}
              {summary.corrections > 0 && (
                <HeaderStat>
                  <div className="top">
                    <span className="label">Corrections</span>
                    <span className="value">{summary.corrections}</span>
                  </div>
                  <span className="sub">Not counted as changes</span>
                </HeaderStat>
              )}
            </>
          )}
        </HeaderStats>
      </Header>

      <Content>
        <MainSection>
          <FiltersBar>
            <ClusterSelector
              value={filters.clusterId}
              onChange={(value) => setFilter('clusterId', value || '')}
              label="Cluster"
              size="small"
              fullWidth={false}
              sx={{ minWidth: 180 }}
            />

            <BusinessUnitSelector
              value={filters.businessUnitId}
              onChange={(value) => setFilter('businessUnitId', value || '')}
              label="Business Unit"
              size="small"
              fullWidth={false}
              sx={{ minWidth: 180 }}
            />

            <EmployeeSearchProvider instanceId="change-log-search">
              <EmployeeSearch
                value={selectedEmployee}
                onChange={setSelectedEmployee}
                label="Individual"
                placeholder="Search individuals..."
                width={220}
                height={40}
                hideLabel
              />
            </EmployeeSearchProvider>

            {/* ADR-092: representative (manager) filter + reporting scope chips */}
            <EmployeeSearchProvider instanceId="change-log-representative">
              <EmployeeSearch
                value={representative}
                onChange={setRepresentative}
                label="Representative"
                placeholder="Search representatives..."
                width={220}
                height={40}
                hideLabel
              />
            </EmployeeSearchProvider>

            <EmployeeReportingScope
              selectedScope={representative ? representativeScope : ''}
              onScopeChange={(scope) => representative && setRepresentativeScope(scope)}
              disabledScopes={[EMPLOYEE_REPORTING_SCOPE.ALL]}
              disabled={!representative}
              labelOverrides={{ [EMPLOYEE_REPORTING_SCOPE.HIERARCHY]: 'Hierarchy' }}
            />

            <FormControl size="small" sx={{ minWidth: 190 }}>
              {/* displayEmpty renders a value while the selection is empty, but
                  MUI only shrinks the label when the value is non-empty — so
                  without shrink + notched the label sits ON TOP of "All fields".
                  Every select in this bar is set this way, so the whole row
                  reads the same at rest: floated label, value in the box. */}
              <InputLabel shrink id="change-log-fields-label">What changed</InputLabel>
              <Select
                labelId="change-log-fields-label"
                multiple
                displayEmpty
                value={filters.fieldGroups}
                input={<OutlinedInput notched label="What changed" />}
                onChange={(e) => setFilter('fieldGroups', e.target.value)}
                renderValue={(selected) => (selected.length === 0
                  ? 'All fields'
                  : selected.map((g) => FIELD_GROUP_LABELS[g] || g).join(', '))}
              >
                {Object.keys(FIELD_GROUP_LABELS).map((group) => (
                  <MenuItem key={group} value={group}>
                    <Checkbox checked={filters.fieldGroups.includes(group)} size="small" />
                    <ListItemText
                      primary={FIELD_GROUP_LABELS[group]}
                      secondary={`${FIELD_GROUP_HINTS[group]}${
                        fieldCountByGroup[group] ? ` · ${fieldCountByGroup[group]} fields` : ''}`}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel shrink id="change-log-detector-label">Detector</InputLabel>
              <Select
                labelId="change-log-detector-label"
                multiple
                displayEmpty
                value={filters.detectors}
                input={<OutlinedInput notched label="Detector" />}
                onChange={(e) => setFilter('detectors', e.target.value)}
                renderValue={(selected) => (selected.length === 0
                  ? 'All detectors'
                  : selected.map((d) => DETECTOR_LABELS[d] || d).join(', '))}
              >
                {DETECTOR_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    <Checkbox checked={filters.detectors.includes(o.value)} size="small" />
                    <ListItemText primary={o.label} secondary={o.hint} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel shrink id="change-log-actor-label">Actor</InputLabel>
              <Select
                labelId="change-log-actor-label"
                displayEmpty
                value={filters.actorTypes[0] || ''}
                input={<OutlinedInput notched label="Actor" />}
                onChange={(e) => setFilter('actorTypes', e.target.value ? [e.target.value] : [])}
              >
                {ACTOR_TYPE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>

            {/* ADR-172 D5 — pick a person; the API also matches the employee id
                their pre-ADR-172 rows were written with. Options are the people
                who changed something in this population and period. */}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel shrink id="change-log-changed-by-label">Changed by</InputLabel>
              <Select
                labelId="change-log-changed-by-label"
                displayEmpty
                value={filters.actorId}
                input={<OutlinedInput notched label="Changed by" />}
                onChange={(e) => setFilter('actorId', e.target.value)}
                renderValue={(value) => {
                  if (!value) {
                    return 'Any person';
                  }
                  const picked = actorOptions.find((o) => o.value === value);
                  return picked?.displayName || value;
                }}
              >
                <MenuItem value="">Any person</MenuItem>
                {actorOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    <ListItemText
                      primary={o.displayName || o.value}
                      secondary={`${o.email || o.value} · ${o.records} record${o.records === 1 ? '' : 's'}`}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* ADR-174 §7 — renamed: this filters by who owns the contract's
                record. "Contract" is now the filter that picks a contract. */}
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel shrink id="change-log-ownership-label">Contract ownership</InputLabel>
              <Select
                labelId="change-log-ownership-label"
                displayEmpty
                value={filters.ownership}
                input={<OutlinedInput notched label="Contract ownership" />}
                onChange={(e) => setFilter('ownership', e.target.value)}
              >
                {OWNERSHIP_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>

            {/* ADR-174 §7 — one of the selected individual's contracts */}
            <Tooltip title={selectedEmployee ? '' : 'Select an individual to pick one of their contracts'}>
              <FormControl size="small" sx={{ minWidth: 200 }} disabled={!selectedEmployee}>
                <InputLabel shrink id="change-log-contract-label">Contract</InputLabel>
                <Select
                  labelId="change-log-contract-label"
                  multiple
                  displayEmpty
                  value={filters.employmentEpisodeIds || []}
                  input={<OutlinedInput notched label="Contract" />}
                  onChange={(e) => setFilter('employmentEpisodeIds', e.target.value)}
                  renderValue={(selected) => (selected.length === 0
                    ? 'Any contract'
                    : selected.map((id) => {
                      const episode = personContracts.find((c) => c.id === id);
                      return episode ? (contractIdentityLabel(null, episode) || 'Contract') : 'Contract';
                    }).join('; '))}
                >
                  {personContracts.map((episode) => (
                    <MenuItem key={episode.id} value={episode.id}>
                      <Checkbox checked={(filters.employmentEpisodeIds || []).includes(episode.id)} size="small" />
                      <ListItemText
                        primary={contractIdentityLabel(null, episode) || 'Contract'}
                        secondary={episode.endDate ? 'Ended' : 'Active'}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Tooltip>

            <TextField
              size="small"
              label="Contract number"
              value={contractNumberInput}
              onChange={(e) => setContractNumberInput(e.target.value)}
              onBlur={commitContractNumber}
              onKeyDown={(e) => { if (e.key === 'Enter') commitContractNumber(); }}
              placeholder="Exact number"
              InputLabelProps={{ shrink: true }}
              sx={{ width: 170 }}
            />

            <FormControlLabel
              control={(
                <Switch
                  size="small"
                  checked={Boolean(filters.concurrentOnly)}
                  onChange={(e) => setFilter('concurrentOnly', e.target.checked)}
                />
              )}
              label="Concurrent contracts only"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: 13 } }}
            />

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel shrink id="change-log-amendment-link-label">Explaining event</InputLabel>
              <Select
                labelId="change-log-amendment-link-label"
                displayEmpty
                value={filters.explanation}
                input={<OutlinedInput notched label="Explaining event" />}
                onChange={(e) => setFilter('explanation', e.target.value)}
              >
                {EXPLANATION_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>

            <Tooltip title="Departed people are included by default — a change log that hides them is not a change log.">
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel shrink id="change-log-status-label">Status</InputLabel>
                <Select
                  labelId="change-log-status-label"
                  displayEmpty
                  value={filters.organizationalStatus}
                  input={<OutlinedInput notched label="Status" />}
                  onChange={(e) => setFilter('organizationalStatus', e.target.value)}
                >
                  {STATUS_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Tooltip>

            <FormControl size="small" sx={{ minWidth: 165 }}>
              <InputLabel shrink id="change-log-window-label">Detected</InputLabel>
              <Select
                labelId="change-log-window-label"
                value={filters.windowDays}
                input={<OutlinedInput notched label="Detected" />}
                onChange={(e) => setFilter('windowDays', e.target.value)}
              >
                {WINDOW_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControlLabel
              control={(
                <Switch
                  size="small"
                  checked={filters.effectiveOnly}
                  onChange={(e) => setFilter('effectiveOnly', e.target.checked)}
                />
              )}
              label="Effective date set only"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: 13 } }}
            />

            {/* ADR-112 — the scheduled-changes lane selector. Hidden by default.
                Named for what the lane ADDS (scheduled changes, absent from the
                record list) — applied amendments are already in the report as
                their explained records, so "amendments" would be wrong here. */}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel shrink id="change-log-amendments-label">Scheduled changes</InputLabel>
              <Select
                labelId="change-log-amendments-label"
                value={amendmentsLane}
                input={<OutlinedInput notched label="Scheduled changes" />}
                onChange={(e) => setFilter('amendmentsLane', e.target.value)}
              >
                {AMENDMENTS_LANE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </FiltersBar>

          {/* §5.4 — an effective-date filter silently drops every sweep row and
              every hook row. The view says so while it is on. */}
          {meta?.effectiveOnlyExcludesUndated && (
            <Alert severity="warning" sx={{ mb: 2, fontSize: 13 }}>
              Rows with no effective date are excluded. The nightly sweep and both update
              hooks record none, so this hides most of the log.
            </Alert>
          )}

          {summary && (
            <IntegrityPanel>
              <div className="title">
                Unexplained term movement — {summary.unexplainedTerms} record
                {summary.unexplainedTerms === 1 ? '' : 's'} with no explaining event behind
                {summary.unexplainedTerms === 1 ? ' it' : ' them'}
              </div>
              <div className="hint">
                Machine rows on the synced populations are expected drift. Human rows are the finding.
              </div>
              <div className="chips">
                {summary.integrityGroups.length === 0 ? (
                  <Chip
                    size="small"
                    label="Every term movement in this period is explained by an amendment"
                    sx={getChipSx('success')}
                  />
                ) : summary.integrityGroups.map((group) => (
                  <Chip
                    key={`${group.field}-${group.actorType}`}
                    size="small"
                    label={`${fieldLabel(group.field)} · ${group.actorType === 'User' ? 'human' : 'machine'}: ${group.count}`}
                    sx={getChipSx(group.actorType === 'User' ? 'error' : 'default')}
                  />
                ))}
                {summary.unattributedActors > 0 && (
                  <Tooltip title="Changes whose stored actor matches no consultant. New writes cannot produce these; investigate the source.">
                    <Chip
                      size="small"
                      label={`Unattributed actor: ${summary.unattributedActors}`}
                      sx={getChipSx('error')}
                    />
                  </Tooltip>
                )}
              </div>
              {/* §8 — the aggregation boundary, printed rather than implied. */}
              <div className="boundary">
                Header and panel counts cover the population and period selected above —
                {' '}not narrowed by the change, provenance or amendment filters.
              </div>
            </IntegrityPanel>
          )}

          {/* ADR-112 — the amendments lane: EVENT-sourced (never change
              records, ADR-147 §7); its counts are never added to the changes
              below; the Episodes timeline stays the audit of record. */}
          {amendmentsLane !== 'hidden' && amendments && (
            <AmendmentsLanePanel>
              <div className="title">
                Scheduled changes — {amendments.meta.scheduledCount} upcoming
                {amendments.meta.includeCancelled ? ` · ${amendments.meta.cancelledCount} cancelled` : ''}
              </div>
              <div className="caption">
                Upcoming shows every currently scheduled amendment and contract replacement
                regardless of the period; cancelled follows the period selected above.
                Population and contract filters apply here; the What-changed filter applies to amendments
                and hides replacements (they are not field-keyed); detector, actor and status
                filters do not apply. Scheduled contract ends are not listed — that is the
                Fixed Term Contracts report&apos;s job. These are intents, not changes — they
                are never counted in the list below, and each is audited in full on the
                person&apos;s Employment Episodes tab.
              </div>

              {amendments.scheduled.length === 0 &&
                (!amendments.meta.includeCancelled || amendments.cancelled.length === 0) && (
                <div className="empty">
                  Nothing scheduled
                  {amendments.meta.includeCancelled ? ', and nothing cancelled in this period,' : ''}
                  {' '}for this population.
                </div>
              )}

              {amendments.scheduled.length > 0 && (
                <>
                  <div className="sub-title">Upcoming — nearest first</div>
                  {amendments.scheduled.map((row) => (
                    <div className="lane-row" key={row.id}>
                      <div className="main">
                        <span className="name" onClick={() => handleNavigateToEmployee(row)}>
                          {row.employee?.fullName || 'Unknown individual'}
                        </span>
                        {/* ADR-112 §11 phase 2 — replacements interleave with
                            amendments; the row says which mechanism it is. */}
                        {row.kind === 'replacement' ? (
                          <span className="phrase">
                            {/* A replacement is not field-scoped: the mechanism
                                IS the subject of the row, so it carries the
                                emphasis an amendment gives its field. */}
                            <span className="field">Contract replacement</span>
                            {row.contract ? ` of ${contractLabel(row.contract)}` : ''}
                            {/* ADR-116 §3.3 — prose, not a label. The number is
                                embedded in a sentence that already uses an em dash
                                as its separator, so formatContractNumber (which
                                renders `Contract · N`) is deliberately not used. */}
                            {row.contractNumber ? ` — new contract ${row.contractNumber}` : ''}
                            {row.contractEndDate ? `, fixed-term until ${formatDay(row.contractEndDate)}` : ''}
                          </span>
                        ) : (
                          <span className="phrase">
                            {/* ADR-174 §4 — #N is THIS contract's sequence,
                                so the contract is always named */}
                            {amendmentHeading(row)}
                            {': '}{amendmentPhrases(row, fieldLabel)}
                            {showPrimaryChip(row.contract) && (
                              <Chip size="small" label="Primary" sx={{ ml: 1, height: 18, fontSize: 11, backgroundColor: '#e0e7ff', color: '#4f46e5' }} />
                            )}
                          </span>
                        )}
                        <span className="sub">
                          raised by {row.raisedBy || 'unknown'} on {formatDate(row.raisedAt)}
                          {row.reason ? ` — ${row.reason}` : ''}
                        </span>
                      </div>
                      <div className="side">
                        <Chip
                          size="small"
                          label={row.kind === 'replacement'
                            ? `Scheduled · cutover ${formatDay(row.effectiveOn)}`
                            : `Scheduled · effective ${formatDay(row.effectiveOn)}`}
                          sx={getChipSx('warning')}
                        />
                      </div>
                    </div>
                  ))}
                </>
              )}

              {amendments.meta.includeCancelled && amendments.cancelled.length > 0 && (
                <>
                  <div className="sub-title">Cancelled — never took effect</div>
                  {amendments.cancelled.map((row) => (
                    <div className="lane-row" key={`cancelled-${row.id}`}>
                      <div className="main">
                        <span className="name" onClick={() => handleNavigateToEmployee(row)}>
                          {row.employee?.fullName || 'Unknown individual'}
                        </span>
                        {row.kind === 'replacement' ? (
                          <span className="phrase">
                            {/* A replacement is not field-scoped: the mechanism
                                IS the subject of the row, so it carries the
                                emphasis an amendment gives its field. */}
                            <span className="field">Contract replacement</span>
                            {row.contract ? ` of ${contractLabel(row.contract)}` : ''}
                            {/* ADR-116 §3.3 — prose, not a label. The number is
                                embedded in a sentence that already uses an em dash
                                as its separator, so formatContractNumber (which
                                renders `Contract · N`) is deliberately not used. */}
                            {row.contractNumber ? ` — new contract ${row.contractNumber}` : ''}
                            {' '}(was to happen at the {formatDay(row.effectiveOn)} cutover)
                          </span>
                        ) : (
                          <span className="phrase">
                            {amendmentHeading(row)}
                            {': '}{amendmentPhrases(row, fieldLabel)}
                            {' '}(was to take effect {formatDay(row.effectiveOn)})
                          </span>
                        )}
                        <span className="sub">
                          cancelled {formatDate(row.cancelled?.at)} by {row.cancelled?.by || 'the system'}
                          {row.cancelled?.reason ? ` — ${row.cancelled.reason}` : ''}
                        </span>
                      </div>
                      <div className="side">
                        <Chip
                          size="small"
                          label={AMENDMENT_CANCEL_CAUSE_LABELS[row.cancelled?.cause] || 'Cancelled'}
                          sx={getChipSx('default')}
                        />
                      </div>
                    </div>
                  ))}
                </>
              )}
            </AmendmentsLanePanel>
          )}

          {isLoading && rows.length === 0 ? (
            <LoadingSpinner />
          ) : rows.length === 0 ? (
            <EmptyState
              title="No Change Records Found"
              description="No detected changes match the current filters. Try widening the period or clearing a provenance filter."
            />
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <ResultsCount count={pageInfo.filteredTotal ?? rows.length} singular="change" plural="changes" />
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={viewMode}
                    onChange={(e, next) => { if (next) setViewMode(next); }}
                    aria-label="View mode"
                  >
                    <ToggleButton value="cards" sx={{ textTransform: 'none', px: 1.5, py: 0.25 }}>Cards</ToggleButton>
                    <ToggleButton value="table" sx={{ textTransform: 'none', px: 1.5, py: 0.25 }}>Table</ToggleButton>
                  </ToggleButtonGroup>
                  <SortButton onClick={() => setFilter('orderDirection', filters.orderDirection === 'desc' ? 'asc' : 'desc')}>
                    {filters.orderDirection === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />}
                    Detected ({filters.orderDirection === 'asc' ? 'oldest' : 'newest'} first)
                  </SortButton>
                </div>
              </div>

              {viewMode === 'table' && (
                <TableContainer sx={{ mb: 1, overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Detected</TableCell>
                        <TableCell>Individual</TableCell>
                        <TableCell>Contract</TableCell>
                        <TableCell>Field</TableCell>
                        <TableCell>Change</TableCell>
                        <TableCell>Detector</TableCell>
                        <TableCell>Actor</TableCell>
                        <TableCell>Explained by</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rowGroups.flatMap((group) => group.rows.map((row) => {
                        const finding = row.unexplainedTerm && row.actor?.type === 'User';
                        // The correlation band, table idiom: rows written by ONE
                        // cause carry a continuous rail on the leading cell —
                        // the same information the card view's ledger-margin
                        // band conveys (ADR-145 §2), not card decoration.
                        const banded = group.rows.length > 1;
                        return (
                          <TableRow key={row.id} hover>
                            <TableCell
                              sx={{
                                whiteSpace: 'nowrap',
                                borderLeft: banded ? '3px solid #cbd5e1' : '3px solid transparent',
                              }}
                            >{formatDateTime(row.detectedAt)}</TableCell>
                            <TableCell>
                              <span
                                className="name-link"
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleNavigateToEmployee(row)}
                              >
                                {row.employee?.fullName || 'Unknown individual'}
                              </span>
                            </TableCell>
                            {/* ADR-174 §3.1 — the contract the change is on */}
                            <TableCell>
                              {row.contract ? (
                                <Tooltip title={ownershipLabel(row.contract)}>
                                  <span>
                                    {contractLabel(row.contract)}
                                    {showPrimaryChip(row.contract) ? ' · Primary' : ''}
                                  </span>
                                </Tooltip>
                              ) : '—'}
                            </TableCell>
                            <TableCell>{row.fieldLabel}</TableCell>
                            <TableCell>
                              <ValueText value={row.previous} detector={row.detector} side="previous" />
                              {' → '}
                              <ValueText value={row.next} detector={row.detector} side="next" />
                            </TableCell>
                            <TableCell>{DETECTOR_LABELS[row.detector] || row.detector}</TableCell>
                            <TableCell>
                              <ActorChip actor={row.actor} />
                            </TableCell>
                            <TableCell>
                              {row.explained
                                ? <Chip size="small" label="Explained" sx={getChipSx('success')} />
                                : finding
                                  ? <Chip size="small" label="Unexplained" sx={getChipSx('error')} />
                                  : '—'}
                            </TableCell>
                          </TableRow>
                        );
                      }))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {viewMode === 'cards' && <>

              {rowGroups.map((group) => {
                const rowNodes = group.rows.map((row, indexInGroup) => {
                const machine = row.actor?.type === 'System';
                const finding = row.unexplainedTerm && row.actor?.type === 'User';
                const sameCause = indexInGroup > 0;

                return (
                  <React.Fragment key={row.id}>
                    {sameCause && (
                      <CorrelationNote>same change, recorded on another field</CorrelationNote>
                    )}
                    <ChangeRow
                      $machine={machine}
                      onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setExpandedId(expandedId === row.id ? null : row.id);
                        }
                      }}
                    >
                      <ChangeRowMain>
                        <EmployeeAvatar employee={row.employee} size={40} />

                        <ChangeInfo>
                          <div
                            className="name name-link"
                            onClick={(e) => { e.stopPropagation(); handleNavigateToEmployee(row); }}
                          >
                            {row.employee?.fullName || 'Unknown individual'}
                          </div>
                          <div className="detail">
                            {/*
                              The field is what the record is ABOUT; every other
                              segment on this line is provenance. It carries the
                              emphasis, so the line is rendered as nodes rather
                              than one joined string.
                            */}
                            {[
                              ['cluster', row.employee?.cluster?.name],
                              ['field', row.fieldLabel
                                ? <span className="field">{row.fieldLabel}</span>
                                : null],
                              ['detector', DETECTOR_LABELS[row.detector] || row.detector],
                              ['actor', machine ? 'Machine' : `Changed by ${actorLabel(row.actor)}`],
                              // ADR-174 §3.1 (Q6, Option A) — the contract's
                              // identity takes the slot; ownership moves to its
                              // tooltip and the expanded panel.
                              ['contract', row.contract
                                ? (
                                  <Tooltip title={ownershipLabel(row.contract)}>
                                    <span className="contract">
                                      {contractLabel(row.contract)}
                                      {showPrimaryChip(row.contract) && (
                                        <Chip size="small" label="Primary" sx={{ ml: 0.75, height: 18, fontSize: 11, backgroundColor: '#e0e7ff', color: '#4f46e5' }} />
                                      )}
                                      {canAddNumber(row.contract) && (
                                        <span
                                          role="link"
                                          tabIndex={0}
                                          className="name-link"
                                          style={{ marginLeft: 6, fontSize: 12 }}
                                          onClick={(e) => { e.stopPropagation(); openContractsTab(row); }}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') { e.stopPropagation(); openContractsTab(row); }
                                          }}
                                        >
                                          add number
                                        </span>
                                      )}
                                    </span>
                                  </Tooltip>
                                )
                                : 'Person fact — no contract'],
                            ].filter(([, part]) => part).map(([key, part], indexInLine) => (
                              <React.Fragment key={key}>
                                {indexInLine > 0 && ' • '}
                                {part}
                              </React.Fragment>
                            ))}
                          </div>
                          <div className="movement">
                            <ValueText value={row.previous} detector={row.detector} side="previous" />
                            <span className="arrow">→</span>
                            <ValueText value={row.next} detector={row.detector} side="next" />
                          </div>
                        </ChangeInfo>

                        <ChangeMetaCell>
                          <span className="when">{formatDateTime(row.detectedAt)}</span>
                          {row.effectiveOn && (
                            <span className="effective">effective {formatDay(row.effectiveOn)}</span>
                          )}
                          {row.explained && (
                            <Chip size="small" label="Explained" sx={getChipSx('success')} />
                          )}
                          {finding && (
                            <Chip size="small" label="Unexplained" sx={getChipSx('error')} />
                          )}
                          {row.isCorrection && (
                            <Chip size="small" label="Correction" sx={getChipSx('default')} />
                          )}
                          {machine && !row.explained && !row.isCorrection && (
                            <Chip size="small" label="Expected drift" sx={getChipSx('default')} />
                          )}
                        </ChangeMetaCell>
                      </ChangeRowMain>
                    </ChangeRow>

                    {expandedId === row.id && (
                      <DetailPanel>
                        <div><dt>Record</dt><dd>{row.id}</dd></div>
                        <div><dt>Cause</dt><dd>{row.correlationId || '—'}</dd></div>
                        <div><dt>Detector</dt><dd>{DETECTOR_LABELS[row.detector] || row.detector}</dd></div>
                        <div><dt>Actor</dt><dd>{machine ? actorDetail(row.actor) : `${actorLabel(row.actor)} · ${actorDetail(row.actor)} · Human`}</dd></div>
                        <div>
                          <dt>Contract</dt>
                          <dd>
                            {row.contract ? (
                              <>
                                {contractLabel(row.contract)}
                                {' · '}
                                {formatDay(row.contract.startDate) || '—'}
                                {' – '}
                                {row.contract.endDate ? formatDay(row.contract.endDate) : 'Present'}
                                {row.contract.isPrimary && !row.contract.endDate ? ' · primary today' : ''}
                                {' · '}
                                {ownershipLabel(row.contract)}
                                {' · '}
                                <span style={{ color: '#94a3b8', fontSize: 11 }}>{row.contract.episodeId}</span>
                                {' · '}
                                <span
                                  role="link"
                                  tabIndex={0}
                                  className="name-link"
                                  onClick={(e) => { e.stopPropagation(); openContractsTab(row); }}
                                  onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); openContractsTab(row); } }}
                                >
                                  Open on Contracts tab
                                </span>
                              </>
                            ) : '— person fact, no contract'}
                          </dd>
                        </div>
                        <div>
                          <dt>Explained by</dt>
                          <dd>{row.explainedBy ? `${explainingEventLabel(row.explainedBy)} · ${row.explainedBy.id}` : '— nothing'}</dd>
                        </div>
                        <p className="note">{detailNote(row)}</p>
                      </DetailPanel>
                    )}
                  </React.Fragment>
                );
                });

                return group.rows.length > 1
                  ? <CorrelationGroup key={group.key}>{rowNodes}</CorrelationGroup>
                  : <React.Fragment key={group.key}>{rowNodes}</React.Fragment>;
              })}
              </>}

              {pageInfo.hasNextPage && (
                <LoadMoreButton
                  type="button"
                  disabled={isLoading}
                  onClick={() => load({ cursor: pageInfo.endCursor })}
                >
                  {isLoading ? 'Loading…' : 'Load more'}
                </LoadMoreButton>
              )}
            </>
          )}

          <FootNote>
            Change records are telemetry, not the contract&apos;s audit of record — amendments
            live on the episode timeline as events. Nothing here can be acknowledged, dismissed,
            edited or exported: the log is read on screen, because the scoping that governs it is
            enforced at the query and a file would carry none of it.
            {/* Owner's call 2026-08-26: records are KEPT — no prune runs or is
                planned. The 400 days is the report's reach, not a deletion
                policy, and the copy must not claim a process that does not
                exist. */}
            {meta?.retentionDays ? ` The report reaches back at most ${meta.retentionDays} days.` : ''}
          </FootNote>
        </MainSection>
      </Content>
    </Container>
  );
};

export default EmploymentChangeLogReport;
